/**
 * The invitee's store in IndexedDB (part 38c, `store.ts`): one database, `cockatrice-shared`, with four stores (the
 * pending invite, keyrings by account, claims by `account invite`, items by `account invite item`). Every value is read
 * back through `store.ts`'s readers; a write that doesn't commit rejects with {@link SharedStoreError}. A later build's
 * upgrade is never kept waiting: the connection closes on `versionchange`.
 */
import { encodeShareNote } from '@cockatrice/core';
import { claimKey, claimValue, itemKey, readCachedClaim, readCachedItem, readHeldKeyring, readPending, under } from './store.ts';
import type { CachedClaim, CachedItem, HeldKeyring, PendingInvite, SharedStore } from './store.ts';

/** The database's name and version (1: four stores). */
export const SHARED_DB = 'cockatrice-shared';
const VERSION = 1;

const PENDING = 'pending';
const KEYRINGS = 'keyrings';
const CLAIMS = 'claims';
const ITEMS = 'items';
const PENDING_KEY = 'invite';

/** A write that didn't commit. */
export class SharedStoreError extends Error {
  override readonly name = 'SharedStoreError';

  constructor(cause: unknown) {
    super('a write to the shared store was not kept', { cause });
  }
}

function done<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = () => {
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- the browser's own error, passed through unchanged
      reject(request.error);
    };
  });
}

function committed(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => {
      resolve();
    };
    transaction.onabort = () => {
      reject(new SharedStoreError(transaction.error));
    };
  });
}

/** Opens the store on `factory` (`indexedDB`); `name` isolates a test. */
export async function openSharedStore(factory: IDBFactory, name: string = SHARED_DB): Promise<SharedStore> {
  const request = factory.open(name, VERSION);
  request.onupgradeneeded = () => {
    for (const store of [PENDING, KEYRINGS, CLAIMS, ITEMS]) request.result.createObjectStore(store);
  };
  const db = await done(request);
  db.onversionchange = () => {
    db.close();
  };
  const read = async <T>(store: string, key: string, reader: (value: unknown) => T | null): Promise<T | null> => reader(await done(db.transaction(store).objectStore(store).get(key)));
  const range = (prefix: string): IDBKeyRange => {
    const [lower, upper] = under(prefix);
    return IDBKeyRange.bound(lower, upper);
  };
  const all = async <T>(store: string, prefix: string, reader: (value: unknown) => T | null): Promise<T[]> => {
    const values: unknown[] = await done(db.transaction(store).objectStore(store).getAll(range(prefix)));
    return values.flatMap((value) => {
      const read_ = reader(value);
      return read_ === null ? [] : [read_];
    });
  };
  const write = async (stores: readonly string[], change: (transaction: IDBTransaction) => void): Promise<void> => {
    const transaction = db.transaction(stores, 'readwrite');
    const kept = committed(transaction);
    change(transaction);
    await kept;
  };
  return {
    pending: () => read(PENDING, PENDING_KEY, readPending),
    keepPending: (pending: PendingInvite) =>
      write([PENDING], (tx) => {
        tx.objectStore(PENDING).put(pending, PENDING_KEY);
      }),
    dropPending: () =>
      write([PENDING], (tx) => {
        tx.objectStore(PENDING).delete(PENDING_KEY);
      }),
    keyring: (account: string) => read(KEYRINGS, account, readHeldKeyring),
    keepKeyring: (account: string, keyring: HeldKeyring) =>
      write([KEYRINGS], (tx) => {
        tx.objectStore(KEYRINGS).put(keyring, account);
      }),
    claims: (account: string) => all(CLAIMS, account, readCachedClaim),
    keepClaim: (account: string, claim: CachedClaim) =>
      write([CLAIMS], (tx) => {
        tx.objectStore(CLAIMS).put(claimValue(claim, encodeShareNote), claimKey(account, claim.invite));
      }),
    items: (account: string, invite: string) => all(ITEMS, claimKey(account, invite), readCachedItem),
    keepItem: (account: string, invite: string, item: CachedItem) =>
      write([ITEMS], (tx) => {
        tx.objectStore(ITEMS).put(item, itemKey(account, invite, item.item));
      }),
    dropItems: (account: string, invite: string, items: readonly string[]) =>
      write([ITEMS], (tx) => {
        for (const item of items) tx.objectStore(ITEMS).delete(itemKey(account, invite, item));
      }),
    dropClaims: (account: string, invites: readonly string[]) =>
      write([CLAIMS, ITEMS], (tx) => {
        for (const invite of invites) {
          tx.objectStore(CLAIMS).delete(claimKey(account, invite));
          tx.objectStore(ITEMS).delete(range(claimKey(account, invite)));
        }
      }),
    accounts: async () => {
      const tx = db.transaction([KEYRINGS, CLAIMS]);
      const keys = [...(await done(tx.objectStore(KEYRINGS).getAllKeys())), ...(await done(tx.objectStore(CLAIMS).getAllKeys()))];
      // A keyring's key is the account; a claim's `account invite`: the account is what comes before its space.
      return [
        ...new Set(
          keys.filter((key): key is string => typeof key === 'string').map((text) => {
            const space = text.indexOf(' ');
            return space < 0 ? text : text.slice(0, space);
          }),
        ),
      ];
    },
    clear: (account: string) =>
      write([KEYRINGS, CLAIMS, ITEMS], (tx) => {
        tx.objectStore(KEYRINGS).delete(account);
        tx.objectStore(CLAIMS).delete(range(account));
        tx.objectStore(ITEMS).delete(range(account));
      }),
    close: () => {
      db.close();
    },
  };
}
